import { Box, Flex, HStack, IconButton, NumberInput, NumberInputField, Switch, Text } from 'design-system';
import { FiClock, FiX } from 'react-icons/fi';

export interface MatrixViewControlsProps {
  visibleCount: number;
  totalCount: number;
  recentDaysInput: string;
  isRecentFilterOn: boolean;
  onRecentDaysInputChange: (value: string) => void;
  isPricedOnly: boolean;
  onPricedOnlyChange: (value: boolean) => void;
}

// Table toolbar strip between the facet header and the grid. The attribute
// chips above pick WHICH variants exist in the view; these controls decide
// which ROWS of the table are worth showing (price recency, priced coverage) —
// so they live on the table, styled as one segmented control card (square-ish
// radius, icon + switch affordances) that can't be read as another facet pill
// row. The SKU count sits beside them: it is the number these controls change.
export const MatrixViewControls = ({
  visibleCount,
  totalCount,
  recentDaysInput,
  isRecentFilterOn,
  onRecentDaysInputChange,
  isPricedOnly,
  onPricedOnlyChange,
}: MatrixViewControlsProps) => {
  return (
    <Flex
      align='center'
      justify='space-between'
      gap='12px'
      wrap='wrap'
      rowGap='8px'
      px={{ base: '14px', md: '26px' }}
      py='8px'
      bg='#FAFAFB'
      borderBottom='1px solid'
      borderColor='gray.200'>
      <Text
        fontSize='11px'
        fontWeight='semibold'
        letterSpacing='0.08em'
        textTransform='uppercase'
        color='gray.500'
        sx={{ fontVariantNumeric: 'tabular-nums' }}>
        Showing {visibleCount} of {totalCount} SKUs
      </Text>

      <HStack
        spacing={0}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        bg='white'
        border='1px solid'
        borderColor='gray.200'
        borderRadius='10px'
        boxShadow='0 1px 2px rgba(16, 24, 40, 0.06)'
        overflow='hidden'>
        <HStack
          spacing='7px'
          px='12px'
          h='38px'
          bg={isRecentFilterOn ? 'red.50' : 'white'}
          transition='background 0.15s'>
          <Box color={isRecentFilterOn ? 'red.500' : 'gray.400'} display='flex'>
            <FiClock size={13} />
          </Box>
          <Text
            fontSize='12.5px'
            fontWeight='medium'
            color={isRecentFilterOn ? 'red.700' : 'gray.600'}
            whiteSpace='nowrap'>
            Updated in last
          </Text>
          <NumberInput
            size='xs'
            w='58px'
            min={0}
            max={9999}
            value={recentDaysInput}
            // Whole days only, capped at 4 digits (9999).
            onChange={(valueAsString) => onRecentDaysInputChange(valueAsString.replace(/\D/g, '').slice(0, 4))}>
            <NumberInputField
              bg='white'
              borderRadius='7px'
              textAlign='center'
              fontWeight='semibold'
              fontSize='13px'
              placeholder='N'
              // The default field padding reserves space for stepper buttons
              // this input doesn't render, clipping numbers past one digit.
              px='6px'
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            />
          </NumberInput>
          <Text fontSize='12.5px' fontWeight='medium' color={isRecentFilterOn ? 'red.700' : 'gray.600'}>
            days
          </Text>
          {isRecentFilterOn && (
            <IconButton
              aria-label='Clear the recent-price filter'
              title='Clear'
              icon={<FiX size={12} />}
              size='xs'
              variant='ghost'
              colorScheme='red'
              borderRadius='full'
              minW='20px'
              h='20px'
              onClick={() => onRecentDaysInputChange('')}
            />
          )}
        </HStack>

        <Box w='1px' alignSelf='stretch' bg='gray.200' />

        <HStack
          as='label'
          spacing='8px'
          px='12px'
          h='38px'
          cursor='pointer'
          bg={isPricedOnly ? 'red.50' : 'white'}
          transition='background 0.15s'>
          <Switch
            size='sm'
            colorScheme='red'
            isChecked={isPricedOnly}
            onChange={(e) => onPricedOnlyChange(e.target.checked)}
          />
          <Text fontSize='12.5px' fontWeight='medium' color={isPricedOnly ? 'red.700' : 'gray.600'} whiteSpace='nowrap'>
            Priced only
          </Text>
        </HStack>
      </HStack>
    </Flex>
  );
};
